import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { CurrentUser, EmslUserType } from '@/types/auth';
import { sessionsApi } from '@/services/api/sessions';

export const AUTH_STORAGE_KEYS = {
  user: 'user',
  bearer: 'bearer',
} as const;

function readJson<T>(key: string): T | null {
  const raw = sessionStorage.getItem(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  sessionStorage.setItem(key, JSON.stringify(value));
}

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<CurrentUser | null>(null);
  const token = ref<string | null>(null);
  const authenticationError = ref(false);

  const isLoggedIn = computed(() => currentUser.value !== null && token.value !== null);

  const emslUserType = computed<EmslUserType | null>(
    () => currentUser.value?.emsl_user_type_name ?? null,
  );

  const isLabUser = computed(() => {
    const u = currentUser.value;
    if (!u) return false;
    return (
      u.emsl_user_type_name === 'Employee' ||
      u.emsl_user_type_name === 'Administrator' ||
      u.organization_id === 1
    );
  });

  const isLabAdmin = computed(
    () => currentUser.value?.emsl_user_type_name === 'Administrator',
  );

  const isSuperAdmin = computed(() => currentUser.value?.organization_admin === 'Y');

  // Legacy `<emsl-nav>` only offered the org switcher when the user belonged to
  // more than one organization (`organizations.length > 1`).
  const organizationCriteria = computed(() => {
    const orgs = currentUser.value?.organizations as { count?: number } | undefined;
    return (orgs?.count ?? 0) > 1;
  });

  const currentOrganization = computed(() => currentUser.value?.organization_id ?? null);

  function setSession(user: CurrentUser | null, bearer: string | null): void {
    if (!user) {
      authenticationError.value = true;
      currentUser.value = null;
      token.value = null;
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.user);
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.bearer);
      return;
    }

    currentUser.value = user;
    token.value = bearer;
    authenticationError.value = false;
    writeJson(AUTH_STORAGE_KEYS.user, user);
    writeJson(AUTH_STORAGE_KEYS.bearer, bearer);
  }

  function logout(): void {
    currentUser.value = null;
    token.value = null;
    authenticationError.value = false;
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.user);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.bearer);
  }

  function restoreFromStorage(): void {
    const user = readJson<CurrentUser>(AUTH_STORAGE_KEYS.user);
    const bearer = readJson<string>(AUTH_STORAGE_KEYS.bearer);
    if (user && bearer) {
      currentUser.value = user;
      token.value = bearer;
    } else {
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.user);
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.bearer);
    }
  }

  function flagAuthenticationError(): void {
    authenticationError.value = true;
  }

  async function login(email: string, password: string): Promise<CurrentUser> {
    try {
      const { user, token: bearer } = await sessionsApi.logIn(email, password);
      setSession(user, bearer);
      return user;
    } catch (err) {
      authenticationError.value = true;
      throw err;
    }
  }

  async function remoteLinkLogin(key: string): Promise<CurrentUser> {
    try {
      const { user, token: bearer } = await sessionsApi.remoteLinkLogIn(key);
      setSession(user, bearer);
      return user;
    } catch (err) {
      authenticationError.value = true;
      throw err;
    }
  }

  return {
    currentUser,
    token,
    authenticationError,
    isLoggedIn,
    isLabUser,
    isLabAdmin,
    isSuperAdmin,
    organizationCriteria,
    currentOrganization,
    emslUserType,
    setSession,
    logout,
    restoreFromStorage,
    flagAuthenticationError,
    login,
    remoteLinkLogin,
  };
});
